import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { Card, SectionTitle } from "@/components/common";
import { BankDetails, ReceiptUpload, money, nm } from "@/pages/BookCleaning";
import { RepeatCleaning } from "@/components/Rating";

const LEGACY = { "2b2b": "cl.small", "3b3b": "cl.large" };

export function CleaningCard({ r, role, reload }) {
  const { t, lang } = useI18n();
  const [banks, setBanks] = useState({});
  const c = r.cleaning;
  useEffect(() => { if (c.payment_method === "bank") api.get("/config").then((x) => setBanks(x.data.cleaning?.banks || {})); }, [c.payment_method]);
  const size = c.size === "larger" ? `${t("cl.bigger")} · ${c.bedrooms} ${t("cl.beds")} / ${c.bathrooms} ${t("cl.baths")}` : (c.package ? nm(c.package, lang) : t(LEGACY[c.size] || "cl.label"));
  const total = c.total ?? c.price;
  const paid = c.payment_status === "paid";
  const receipts = (r.files || []).filter((f) => f.stage === "receipt");
  const toggle = async () => {
    try { await api.post(`/requests/${r.id}/payment`, { status: paid ? "pending" : "paid" }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card data-testid="cleaning-card">
      <SectionTitle><span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" />{t("cl.booking")}</span></SectionTitle>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div><dt className="text-xs text-slate-500">{t("cl.s1")}</dt><dd data-testid="cleaning-size">{size}{c.price != null && <span className="font-mono text-slate-500"> · {money(c.price)}</span>}</dd></div>
        <div><dt className="text-xs text-slate-500">{t("cl.total")}</dt><dd className="font-mono font-semibold" data-testid="cleaning-price">{total != null ? money(total) : t("cl.quote")}</dd></div>
        {c.addons?.length > 0 && <div className="col-span-2"><dt className="text-xs text-slate-500">{t("cl.extras")}</dt><dd data-testid="cleaning-addons">{c.addons.map((a) => `${nm(a, lang)} +${money(a.price)}`).join(" · ")}</dd></div>}
        <div><dt className="text-xs text-slate-500">{t("cl.pay_method")}</dt><dd data-testid="cleaning-pay-method">{t(c.payment_method === "bank" ? "cl.bank" : "cl.cash")}{c.bank && ` · ${c.bank}`}</dd></div>
        <div><dt className="text-xs text-slate-500">&nbsp;</dt><dd className="flex flex-wrap gap-1">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${paid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="cleaning-pay-status">{t(paid ? "cl.paid" : "cl.unpaid")}</span>
          {receipts.length > 0 && <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800" data-testid="cleaning-receipt-badge">{t("cl.receipt_done")}</span>}
        </dd></div>
      </dl>
      {c.payment_method === "bank" && role === "customer" && !paid && (
        <div className="mt-4 space-y-3"><BankDetails bank={c.bank} banks={banks} number={r.number} t={t} /><ReceiptUpload rid={r.id} done={receipts.length > 0} onDone={reload} t={t} /></div>
      )}
      {role === "owner" && <Button size="sm" variant="outline" className="mt-4 rounded-full" onClick={toggle} data-testid="cleaning-toggle-paid-btn">{t(paid ? "cl.mark_unpaid" : "cl.mark_paid")}</Button>}
      {role === "customer" && <RepeatCleaning r={r} reload={reload} />}
      {role !== "customer" && c.recurrence && <p className="mt-3 text-xs font-semibold text-blue-700" data-testid="repeat-status">{t(`rp.on_${c.recurrence}`)}</p>}
    </Card>
  );
}
